import React, { useEffect, useState } from 'react';
import {
  Card,
  CardProps,
  CardActionArea,
  CardContent,
  CardContentProps,
  Typography,
  Box,
  IconButton,
  styled,
} from '@mui/material';
import StarIconMui from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { radius } from 'themes/radius';
import {
  RESOURCE_LIBRARY_CARD_WIDTH_MOBILE,
  RESOURCE_LIBRARY_CARD_WIDTH_TABLET,
  RESOURCE_LIBRARY_CARD_IMAGE_HEIGHT_MOBILE,
  RESOURCE_LIBRARY_CARD_IMAGE_HEIGHT_TABLET,
  RESOURCE_LIBRARY_CARD_CONTENT_HEIGHT_MOBILE,
  RESOURCE_LIBRARY_CARD_CONTENT_HEIGHT_TABLET,
} from 'themes/constants';

interface LibraryCardProps {
  title: string;
  imageUrl: string;
  onClick?: () => void;
  disabled?: boolean;
  ariaLabel?: string;
  showFavorite?: boolean;
  isFavorited?: boolean;
  onToggleFavorite?: () => Promise<unknown> | void;
}

const StyledCard = styled(Card)<CardProps>(({ theme }) => ({
  position: 'relative', // anchor the favorite overlay
  borderRadius: radius.card,
  boxShadow: theme.shadows[4],
  width: RESOURCE_LIBRARY_CARD_WIDTH_MOBILE,
  maxWidth: RESOURCE_LIBRARY_CARD_WIDTH_MOBILE,
  [theme.breakpoints.up('sm')]: {
    width: RESOURCE_LIBRARY_CARD_WIDTH_TABLET,
    maxWidth: RESOURCE_LIBRARY_CARD_WIDTH_TABLET,
  },
}));

const StyledCardActionArea = styled(CardActionArea)(() => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
}));

const ImageSection = styled(Box)(({ theme }) => ({
  height: RESOURCE_LIBRARY_CARD_IMAGE_HEIGHT_MOBILE,
  overflow: 'hidden',
  borderTopLeftRadius: radius.card,
  borderTopRightRadius: radius.card,
  flexShrink: 0,
  [theme.breakpoints.up('sm')]: {
    height: RESOURCE_LIBRARY_CARD_IMAGE_HEIGHT_TABLET,
  },
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
}));

const StyledCardContent = styled(CardContent)<CardContentProps>(({ theme }) => ({
  display: 'flex',
  height: RESOURCE_LIBRARY_CARD_CONTENT_HEIGHT_MOBILE,
  padding: `0 ${theme.spacing(2)}`,
  alignItems: 'center',
  [theme.breakpoints.up('sm')]: {
    height: RESOURCE_LIBRARY_CARD_CONTENT_HEIGHT_TABLET,
  },
  '&:last-child': {
    paddingBottom: 0,
  },
}));

const CardTitle = styled(Typography)(() => ({
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}));

// Favorite toggle, overlaid on the image. Kept OUTSIDE CardActionArea so we don't
// nest a button inside a button (invalid HTML + axe `nested-interactive`).
const FavoriteButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1.5),
  right: theme.spacing(1.5),
  zIndex: 1,
  padding: 0,
  width: 30,
  height: 30,
  // Figma: star sits on a white circle. The disc is the button's white bg.
  backgroundColor: theme.palette.common.white,
  boxShadow: theme.shadows[1],
  // Start slightly transparent, animate to fully opaque on hover/focus.
  opacity: 0.85,
  transition: theme.transitions.create(['opacity'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover, &.Mui-focusVisible': {
    backgroundColor: theme.palette.common.white,
    opacity: 1,
  },
}));

/**
 * Cobrand-aware star icon using MUI icons.
 * filled = favorited; outlined = not favorited.
 */
const StarIcon = React.memo<{ filled: boolean }>(({ filled }) => {
  const IconComponent = filled ? StarIconMui : StarBorderIcon;
  return (
    <IconComponent
      aria-hidden
      focusable={false}
      sx={{
        fontSize: 22,
        color: (theme) => theme.palette.secondary.main,
      }}
    />
  );
});

// Required for React.memo to satisfy react/display-name lint rule + DevTools.
StarIcon.displayName = 'StarIcon';

export const LibraryCard: React.FC<LibraryCardProps> = ({
  title,
  imageUrl,
  onClick,
  disabled = false,
  ariaLabel,
  showFavorite = false,
  isFavorited = false,
  onToggleFavorite,
}) => {
  // Optimistic update for performance
  const [localFavorited, setLocalFavorited] = useState(isFavorited);

  // sync local favorited state with prop changes
  useEffect(() => {
    setLocalFavorited(isFavorited);
  }, [isFavorited]);

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = !localFavorited;
    setLocalFavorited(next); // instant feedback
    try {
      await onToggleFavorite?.();
    } catch {
      setLocalFavorited(!next); // rollback on failure (duplicate/invalid url)
    }
  };

  return (
    <StyledCard className="library-card" sx={{ opacity: disabled ? 0.5 : 1 }}>
      {showFavorite ? (
        <FavoriteButton
          className="library-card__favorite"
          type="button"
          onClick={handleFavoriteClick}
          disabled={disabled}
          aria-pressed={localFavorited}
          aria-label={
            localFavorited ? `Remove ${title} from favorites` : `Add ${title} to favorites`
          }
        >
          <StarIcon filled={localFavorited} />
        </FavoriteButton>
      ) : null}

      <StyledCardActionArea
        className="library-card__action-area"
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel || title}
        aria-disabled={disabled}
      >
        <ImageSection className="library-card__image">
          <img src={imageUrl} alt="" />
        </ImageSection>
        <StyledCardContent className="library-card__content">
          <CardTitle className="library-card__title" variant="callout">
            {title}
          </CardTitle>
        </StyledCardContent>
      </StyledCardActionArea>
    </StyledCard>
  );
};
